import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { toast } from 'ngx-sonner';

/**
 * Offline Detection Service
 * Monitors online/offline status and provides methods to detect network connectivity
 */
@Injectable({ providedIn: 'root' })
export class OfflineDetectionService {
    private readonly _isOnline$: BehaviorSubject<boolean>;
    private readonly _connectionType$: BehaviorSubject<string>;
    private readonly _connectionSpeed$: BehaviorSubject<string>;
    private readonly _syncQueue$: BehaviorSubject<'idle' | 'processing'> =
        new BehaviorSubject<'idle' | 'processing'>('idle');
    private offlineToastId: string | number | null = null;

    constructor() {
        this._isOnline$ = new BehaviorSubject<boolean>(navigator.onLine);
        this._connectionType$ = new BehaviorSubject<string>(
            this.getConnectionType(),
        );
        this._connectionSpeed$ = new BehaviorSubject<string>(
            this.getConnectionSpeed(),
        );

        // Listen for online/offline events
        globalThis.addEventListener('online', () => this.handleOnline());
        globalThis.addEventListener('offline', () => this.handleOffline());

        // Listen for connection type changes
        const connection =
            (navigator as any).connection ||
            (navigator as any).mozConnection ||
            (navigator as any).webkitConnection;
        if (connection) {
            connection.addEventListener('change', () =>
                this.handleConnectionChange(),
            );
        }
    }

    /**
     * Observable that emits the online status
     */
    get isOnline$(): Observable<boolean> {
        return this._isOnline$.asObservable();
    }

    /**
     * Observable that emits sync queue processing status
     */
    get syncQueue$(): Observable<'idle' | 'processing'> {
        return this._syncQueue$.asObservable();
    }

    /**
     * Observable that emits the connection type
     */
    get connectionType$(): Observable<string> {
        return this._connectionType$.asObservable();
    }

    /**
     * Observable that emits the connection speed
     */
    get connectionSpeed$(): Observable<string> {
        return this._connectionSpeed$.asObservable();
    }

    /**
     * Get current online status
     */
    get isOnline(): boolean {
        return this._isOnline$.value;
    }

    /**
     * Get current connection type
     */
    get connectionType(): string {
        return this._connectionType$.value;
    }

    /**
     * Get current connection speed
     */
    get connectionSpeed(): string {
        return this._connectionSpeed$.value;
    }

    /**
     * Manually check online status via fetch
     */
    async checkConnectivity(
        url: string = '/assets/ping.png',
    ): Promise<boolean> {
        try {
            const response = await fetch(url, {
                method: 'HEAD',
                no_cors: true,
            } as any);
            return response.ok || response.type === 'opaque';
        } catch {
            return false;
        }
    }

    /**
     * Get detailed connection info
     */
    getConnectionInfo(): {
        isOnline: boolean;
        type: string;
        speed: string;
        effectiveType: string | null;
    } {
        const connection =
            (navigator as any).connection ||
            (navigator as any).mozConnection ||
            (navigator as any).webkitConnection;
        return {
            isOnline: this.isOnline,
            type: this.getConnectionType(),
            speed: this.getConnectionSpeed(),
            effectiveType: connection?.effectiveType || null,
        };
    }

    /**
     * Private methods
     */
    private handleOnline(): void {
        this._isOnline$.next(true);
        this._connectionType$.next(this.getConnectionType());
        this._connectionSpeed$.next(this.getConnectionSpeed());

        // Dismiss offline toast if exists
        if (this.offlineToastId) {
            toast.dismiss(this.offlineToastId);
            this.offlineToastId = null;
        }

        // Emit signal to process sync queue
        this._syncQueue$.next('processing');

        // Show online notification with auto-dismiss (5 seconds)
        toast.success('You are back online', {
            duration: 5000,
            closeButton: true,
        });
    }

    private handleOffline(): void {
        this._isOnline$.next(false);

        // Show offline toast (persistent with dismiss button)
        this.offlineToastId = toast.info(
            'You are offline - viewing cached data',
            {
                closeButton: true,
            },
        );
    }

    private handleConnectionChange(): void {
        this._connectionType$.next(this.getConnectionType());
        this._connectionSpeed$.next(this.getConnectionSpeed());
    }

    private getConnectionType(): string {
        const connection =
            (navigator as any).connection ||
            (navigator as any).mozConnection ||
            (navigator as any).webkitConnection;
        if (!connection) {
            return 'unknown';
        }

        if (connection.effectiveType) {
            return connection.effectiveType;
        }

        if (connection.type) {
            return connection.type;
        }

        return 'unknown';
    }

    private getConnectionSpeed(): string {
        const connection =
            (navigator as any).connection ||
            (navigator as any).mozConnection ||
            (navigator as any).webkitConnection;
        if (!connection) {
            return 'unknown';
        }

        const effectiveType = connection.effectiveType;
        if (effectiveType) {
            const typeMap: { [key: string]: string } = {
                '4g': 'High Speed',
                '3g': 'Medium Speed',
                '2g': 'Low Speed',
                'slow-2g': 'Very Low Speed',
            };
            return typeMap[effectiveType] || effectiveType;
        }

        return 'unknown';
    }
}
